import type { FilterTag, CountryOption, SortOption } from './types'
import { getKeywords, type KeywordsResponse } from './keywords-service'
import { localizeFeatureLabel } from './feature-labels'
import type { UiLanguage } from './types'

export const COUNTRIES: CountryOption[] = [
  { code: 'CZ', name: 'Czech Republic', enabled: true },
  { code: 'DE', name: 'Germany', enabled: false },
  { code: 'PL', name: 'Poland', enabled: false },
]

export const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: 'price_asc', label: 'Price: Low to High' },
  { value: 'price_desc', label: 'Price: High to Low' },
  { value: 'newest', label: 'Date: Newest to Oldest' },
  { value: 'oldest', label: 'Date: Oldest to Newest' },
  { value: 'area_asc', label: 'Area: Smallest' },
  { value: 'area_desc', label: 'Area: Largest' },
  { value: 'rooms_asc', label: 'Rooms: Fewest' },
  { value: 'rooms_desc', label: 'Rooms: Most' },
  { value: 'distance', label: 'Distance' },
]

/**
 * Static filter tags - UI shortcuts that are NOT stored in keywords_cz
 * These are purely UI conveniences for common searches (rooms/price/area ranges)
 * All actual features (balcony, parking, etc.) come from keywords_cz via API
 */
export const STATIC_FILTER_TAGS: FilterTag[] = [
  // Rooms - At least N
  { id: '1+', label: '1+ room', category: 'rooms', value: { field: 'rooms_min', value: 1 } },
  { id: '2+', label: '2+ rooms', category: 'rooms', value: { field: 'rooms_min', value: 2 } },
  { id: '3+', label: '3+ rooms', category: 'rooms', value: { field: 'rooms_min', value: 3 } },
  { id: '4+', label: '4+ rooms', category: 'rooms', value: { field: 'rooms_min', value: 4 } },
  // Rooms - Exactly N
  { id: '1r', label: '1 room', category: 'rooms', value: { field: 'rooms_exact', value: 1 } },
  { id: '2r', label: '2 rooms', category: 'rooms', value: { field: 'rooms_exact', value: 2 } },
  { id: '3r', label: '3 rooms', category: 'rooms', value: { field: 'rooms_exact', value: 3 } },
  { id: '4r', label: '4 rooms', category: 'rooms', value: { field: 'rooms_exact', value: 4 } },
  
  // Price
  { id: '<10k', label: '< 10,000 CZK', category: 'price', value: { field: 'price_max', value: 10000 } },
  { id: '<15k', label: '< 15,000 CZK', category: 'price', value: { field: 'price_max', value: 15000 } },
  { id: '<20k', label: '< 20,000 CZK', category: 'price', value: { field: 'price_max', value: 20000 } },
  { id: '<25k', label: '< 25,000 CZK', category: 'price', value: { field: 'price_max', value: 25000 } },
  { id: '<30k', label: '< 30,000 CZK', category: 'price', value: { field: 'price_max', value: 30000 } },
  { id: '<35k', label: '< 35,000 CZK', category: 'price', value: { field: 'price_max', value: 35000 } },
  { id: '<40k', label: '< 40,000 CZK', category: 'price', value: { field: 'price_max', value: 40000 } },
  
  // Area (m²)
  { id: '>30m', label: '> 30 m²', category: 'area', value: { field: 'area_min', value: 30 } },
  { id: '>50m', label: '> 50 m²', category: 'area', value: { field: 'area_min', value: 50 } },
  { id: '>70m', label: '> 70 m²', category: 'area', value: { field: 'area_min', value: 70 } },
  { id: '>100m', label: '> 100 m²', category: 'area', value: { field: 'area_min', value: 100 } },
  
  // Note: All feature keywords (furnished, ac, pets, balcony, etc.) 
  // now come from keywords_cz database via buildFilterTags()
]

/**
 * Build complete filter tags by merging static tags with dynamic keywords from store
 * This function is called internally by the keywords store
 * 
 * Static tags = UI shortcuts (rooms/price/area ranges)
 * Dynamic tags = All features from keywords_cz (balcony, parking, etc.)
 */
export async function buildFilterTags(
  keywordsData?: KeywordsResponse,
  uiLanguage: UiLanguage = 'cs',
): Promise<FilterTag[]> {
  try {
    const sourceKeywords = keywordsData ?? await getKeywords()
    const dynamicTags: FilterTag[] = []
    
    // Convert keywords to FilterTag format
    Object.values(sourceKeywords.categories).forEach(category => {
      category.keywords.forEach(kw => {
        dynamicTags.push({
          id: kw.id,
          label: localizeFeatureLabel(kw.feature_key, uiLanguage, kw),
          category: category.name as FilterTag['category'],
          value: { field: kw.feature_key as keyof import('./types').RentalFilters, value: true }
        })
      })
    })
    
    // Merge static UI shortcuts with dynamic features from database
    return [...STATIC_FILTER_TAGS, ...dynamicTags]
  } catch (error) {
    console.error('Failed to build filter tags from API, using static tags only:', error)
    return STATIC_FILTER_TAGS
  }
}

/**
 * Get filter tags from the keywords store
 * Returns static tags if store is not yet loaded (for SSR)
 */
export function getFilterTags(): FilterTag[] {
  if (typeof window === 'undefined') {
    // SSR: return static tags
    return STATIC_FILTER_TAGS
  }
  
  // Client-side: get from store if available
  try {
    const { useKeywordsStore } = require('./store')
    const store = useKeywordsStore.getState()
    return store.filterTags.length > 0 ? store.filterTags : STATIC_FILTER_TAGS
  } catch {
    return STATIC_FILTER_TAGS
  }
}

/**
 * Backward compatibility - synchronous export
 * Use getFilterTags() or useKeywordsStore for dynamic tags
 */
export const FILTER_TAGS = STATIC_FILTER_TAGS

// Maximum saved searches a user may keep. A new save past this cap is refused
// in the Save-search dialog; restoring a just-deleted search via Undo (which
// can only bring the count back up to what it was) is exempt.
export const MAX_SAVED_SEARCHES = 5

// Filter slider ranges (shared by filter-tags and filter-modal)
export const ROOMS_MIN = 1
export const ROOMS_MAX = 6
export const ROOMS_STEP = 1
export const PRICE_MIN = 0
export const PRICE_MAX = 80000
export const PRICE_STEP = 1000
// Results grid density. The toolbar slider steps through these column counts
// with EVEN travel per step (5 dense → 1 full-width) — a raw card-size slider
// is reciprocal in columns (container / size), which felt lopsided: a big px
// range for 1–2 columns and a cramped range for the rest. Each stop keeps a
// nominal `size` in the cardSize store field for the compact-card threshold
// (cardSize < 280) and settings compatibility.
export const CARD_SIZE_MIN = 240
export const CARD_SIZE_MAX = 760

export const GRID_COLUMN_STOPS = [
  { columns: 5, size: 260 },
  { columns: 4, size: 320 },
  { columns: 3, size: 400 },
  { columns: 2, size: 540 },
  { columns: 1, size: 760 },
] as const

/** Index of the stop whose nominal size is nearest the stored cardSize. */
export function gridStopIndexForCardSize(cardSize: number): number {
  let best = 0
  for (let i = 1; i < GRID_COLUMN_STOPS.length; i++) {
    if (Math.abs(GRID_COLUMN_STOPS[i].size - cardSize) < Math.abs(GRID_COLUMN_STOPS[best].size - cardSize)) {
      best = i
    }
  }
  return best
}

/** Column count the results grid should render for the stored cardSize. */
export function gridColumnsForCardSize(cardSize: number): number {
  return GRID_COLUMN_STOPS[gridStopIndexForCardSize(cardSize)].columns
}

export const AREA_MIN = 0
// The area slider tops out at 100 m² and shows "Any" beyond it — most listings
// fall under 100 m², so the useful filtering resolution lives in 0–100 and
// larger flats are covered by the open-ended "Any" (area_max cleared).
export const AREA_MAX = 100
export const AREA_STEP = 5

export const TRAVEL_MODES = [
  { value: 'driving', label: 'Driving' },
  { value: 'walking', label: 'Walking' },
]

export const TRAVEL_TIMES = [
  { value: 10, label: '10 min' },
  { value: 15, label: '15 min' },
  { value: 20, label: '20 min' },
  { value: 30, label: '30 min' },
  { value: 45, label: '45 min' },
  { value: 60, label: '60 min' },
]

